import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import {
  IPagerSettings,
  ISistema,
  ISistemaFiltro,
} from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { ISistemasResponse } from './models';

@Injectable({ providedIn: 'root' })
export class SistemasDataService {
  private http = inject(HttpClient);
  private environment = inject(EnvironmentService);
  private baseUrl = `${this.environment.getApiUrl('nomoh')}/sistema`;

  // Estado com Signals (PADRÃO DO PROJETO)
  private _sistemas = signal<ISistema[]>([]);
  private _loading = signal<boolean>(false);
  private _erro = signal<string | null>(null);
  private _paginacao = signal<IPagerSettings>({
    total: 0,
    offSet: 0,
    limit: 20,
    param: '',
    ordenacao: 0,
  });
  private _filtros = signal<Omit<ISistemaFiltro, 'offSet' | 'limit'>>({
    nome: null,
  });

  // Signals públicos (readonly)
  $sistemas = this._sistemas.asReadonly();
  $loading = this._loading.asReadonly();
  $erro = this._erro.asReadonly();
  $paginacao = this._paginacao.asReadonly();
  $filtros = this._filtros.asReadonly();

  // Computed signals
  $temProxPagina = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    const total = pag.total ?? 0;
    return offSet + limit < total;
  });

  $temPagAnterior = computed(() => {
    const offSet = this.$paginacao().offSet ?? 0;
    return offSet > 0;
  });

  $paginaAtual = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    return Math.floor(offSet / limit) + 1;
  });

  $totalPaginas = computed(() => {
    const pag = this.$paginacao();
    const total = pag.total ?? 0;
    const limit = pag.limit ?? 20;
    return Math.ceil(total / limit);
  });

  $infoTotal = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    const total = pag.total ?? 0;
    const inicio = offSet + 1;
    const fim = Math.min(offSet + limit, total);
    return `${inicio}-${fim} de ${total}`;
  });

  // Métodos de estado
  setSistemas(response: ISistemasResponse) {
    if (response.success) {
      this._sistemas.set(response.data);
      this._paginacao.set(response.pagerSettings);
    } else {
      this.setErro(response.errorMessage);
    }
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  setFiltros(filtros: Partial<Omit<ISistemaFiltro, 'offSet' | 'limit'>>) {
    this._filtros.update((current) => ({ ...current, ...filtros }));
  }

  // Operações HTTP + Estado (API: POST /sistema/findAll)
  async carregarSistemas(opcoes?: {
    offSet?: number;
    limit?: number;
  }): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const pag = this.$paginacao();
      const filtroDto: ISistemaFiltro & IPagerSettings = {
        ...this.$filtros(),
        offSet: opcoes?.offSet ?? pag.offSet ?? 0,
        limit: opcoes?.limit ?? pag.limit ?? 20,
        param: pag.param ?? '',
        ordenacao: pag.ordenacao ?? 0,
      };

      const response = await firstValueFrom(
        this.http.post<ISistemasResponse>(`${this.baseUrl}/findAll`, filtroDto)
      );

      this.setSistemas(response);
    } catch (error) {
      this.setErro('Erro ao carregar sistemas');
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  // Operação: Criar sistema (API: POST /sistema/create)
  async criarSistema(sistema: Omit<ISistema, 'id'>): Promise<ISistema | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const created = await firstValueFrom(
        this.http.post<ISistema>(`${this.baseUrl}/create`, sistema)
      );

      // Recarregar lista após criar
      await this.carregarSistemas();
      return created;
    } catch (error) {
      this.setErro('Erro ao criar sistema');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // Operação: Buscar sistema por ID (API: GET /sistema/findById?id={id})
  async buscarPorId(id: string): Promise<ISistema | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const sistema = await firstValueFrom(
        this.http.get<ISistema>(`${this.baseUrl}/findById?id=${id}`)
      );
      return sistema;
    } catch (error) {
      this.setErro('Erro ao buscar sistema');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // Operação: Atualizar sistema (API: PUT /sistema/update)
  async atualizarSistema(sistema: ISistema): Promise<string | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const result = await firstValueFrom(
        this.http.put<string>(`${this.baseUrl}/update`, sistema)
      );

      // Recarregar lista após atualizar
      await this.carregarSistemas();
      return result;
    } catch (error) {
      this.setErro('Erro ao atualizar sistema');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // Operação: Excluir sistema (API: DELETE /sistema/delete/:id)
  async excluirSistema(id: string): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      await firstValueFrom(this.http.delete(`${this.baseUrl}/delete/${id}`));

      // Recarregar lista após excluir
      await this.carregarSistemas();
      return true;
    } catch (error) {
      this.setErro('Erro ao excluir sistema');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  // Navegação de paginação
  async proximaPagina(): Promise<void> {
    if (this.$temProxPagina()) {
      const pag = this.$paginacao();
      const novoPagOffset = (pag.offSet ?? 0) + (pag.limit ?? 20);
      await this.carregarSistemas({ offSet: novoPagOffset });
    }
  }

  async paginaAnterior(): Promise<void> {
    if (this.$temPagAnterior()) {
      const pag = this.$paginacao();
      const novoPagOffset = Math.max(0, (pag.offSet ?? 0) - (pag.limit ?? 20));
      await this.carregarSistemas({ offSet: novoPagOffset });
    }
  }

  async irParaPagina(pagina: number): Promise<void> {
    const limit = this.$paginacao().limit ?? 20;
    const novoOffset = (pagina - 1) * limit;
    await this.carregarSistemas({ offSet: novoOffset });
  }

  async alterarLimit(novoLimit: number): Promise<void> {
    await this.carregarSistemas({ offSet: 0, limit: novoLimit });
  }

  async alterarOrdenacao(campo: string, direcao: 1 | -1): Promise<void> {
    this._paginacao.update((current) => ({
      ...current,
      param: campo,
      ordenacao: direcao,
    }));
    await this.carregarSistemas({ offSet: 0 });
  }

  // Busca com filtros
  async buscar(nome?: string | null): Promise<void> {
    this.setFiltros({ nome });
    await this.carregarSistemas({ offSet: 0 }); // Voltar para primeira página
  }

  // Limpar filtros
  async limparFiltros(): Promise<void> {
    this.setFiltros({ nome: null });
    await this.carregarSistemas({ offSet: 0 });
  }
}
